/**
 * How the UI names and orders unit types, from rules `ui` (schema.ts
 * `UnitUiSchema`), so components don't keep their own per-type tables.
 */
import type { RuleSet } from './schema';

/** A unit type's short roster tag ("Wk"): `ui.glyph`, else its id's first two letters. */
function unitGlyph(rules: RuleSet, type: string): string {
  return rules.units[type]?.ui?.glyph ?? type.slice(0, 2);
}

/** A unit type's plural name: `ui.plural`, else "Archers", "Spies". */
function unitPlural(rules: RuleSet, type: string): string {
  const def = rules.units[type];
  if (def?.ui?.plural) return def.ui.plural;
  const name = def?.name ?? type;
  return /[^aeiou]y$/i.test(name) ? `${name.slice(0, -1)}ies` : `${name}s`;
}

/**
 * Every unit type in roster order: by `ui.order` (types without one after
 * those with one), ties and the rest in rules file order.
 */
function unitTypeOrder(rules: RuleSet): string[] {
  const types = Object.keys(rules.units);
  const order = (type: string) => rules.units[type]?.ui?.order ?? Number.POSITIVE_INFINITY;
  return types
    .map((type, i) => ({ type, i }))
    .sort((a, b) => order(a.type) - order(b.type) || a.i - b.i)
    .map(({ type }) => type);
}

export { unitGlyph, unitPlural, unitTypeOrder };
